import React, { FC, useEffect } from 'react';

import { NativeResources } from '@ariksa/inventory-core/api';
import { Stack } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { colorOpacity, customTheme } from 'theme';

import { formatBytes, Tag } from 'components/DataDisplay';
import { DetailsIdentifier } from 'components/DataDisplay/Utils/DetailsIdentifier';
import { FootprintBox } from 'components/DataDisplay/Utils/FootprintBox';
import formatNumber from 'components/DataDisplay/Utils/formatNumber';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';
import { actions } from 'containers/ActiveCloudResource/slice';
import { formatDate } from 'utils/date';
import { toTitleCase } from 'utils/string';

import {
  getLastScanTimestamp,
  getStatusLabel,
  isDatabaseResource,
  isRepositoryResource,
} from './utils';

export const DataSummary: FC = () => {
  const dispatch = useDispatch();
  const {
    data,
    resourceType,
    resourceUuid,
    resourceDetails,
    resourceInsight,
  } = useSelector(selectActiveResource);
  const { data: summaryData, isLoading } = data.summary;
  const {
    data: resourcesByGroupData,
    isLoading: isLoadingResourcesByGroup,
  } = data.resourcesByGroup;
  const isRepository = isRepositoryResource(resourceType);
  const isDatabase = isDatabaseResource(resourceType);

  useEffect(() => {
    if ((!isRepository && !isDatabase) || !resourceUuid) return;

    dispatch(
      actions.getResourcesByGroup({
        q: {
          nativeResource: resourceType,
          sourceUUIDs: [resourceUuid],
          page: 1,
          size: 1,
        },
      }),
    );
  }, [dispatch, isDatabase, isRepository, resourceType, resourceUuid]);

  const getLabel = () => {
    switch (resourceType) {
      case NativeResources.SimpleStorageService:
        return 'bucket';
      case NativeResources.GitHubRepository:
      case NativeResources.GitLabProject:
      case NativeResources.BitBucketRepository:
        return 'repository';
      default:
        return 'database';
    }
  };

  const scanResource = resourcesByGroupData?.items?.[0];
  const scanStatus = getStatusLabel(
    scanResource?.scan_status ??
      resourceDetails.data?.config?.scan_status ??
      resourceDetails.data?.scan_status ??
      resourceInsight.data?.scan_status,
  );
  const lastScan = getLastScanTimestamp(
    scanResource,
    resourceDetails.data,
    resourceInsight.data,
  );
  const replicationState =
    resourceDetails.data?.config?.replication_state ??
    resourceDetails.data?.config?.replication_status ??
    resourceDetails.data?.replication_state ??
    resourceDetails.data?.replication_status ??
    scanResource?.extra_config?.replication_state ??
    scanResource?.extra_config?.replication_status ??
    '-';

  return (
    <FootprintBox
      content={
        <Stack w="full" h="full" spacing={3}>
          {isRepository ? (
            <>
              <DetailsIdentifier
                label="Repository Size"
                value={formatBytes(summaryData?.source_size)}
              />
              <DetailsIdentifier
                label="Files"
                value={summaryData?.object_count}
              />
              <DetailsIdentifier
                label="Status"
                value={
                  scanStatus === '-' ? (
                    '-'
                  ) : (
                    <Tag
                      label={scanStatus}
                      styles={{
                        tag: {
                          bg: colorOpacity(customTheme.colors.blue['300'], 0.1),
                          border: 'none',
                        },
                        label: { color: customTheme.colors.blue['300'] },
                      }}
                    />
                  )
                }
              />
              <DetailsIdentifier
                label="Last Scan"
                value={formatDate(lastScan, 'DD MMMM, YYYY')}
              />
            </>
          ) : isDatabase ? (
            <>
              <DetailsIdentifier
                label="Database Size"
                value={formatBytes(summaryData?.source_size)}
              />
              <DetailsIdentifier
                label="Number of Tables"
                value={
                  summaryData?.object_count == null
                    ? '-'
                    : `${formatNumber(summaryData.object_count)} tables`
                }
              />
              <DetailsIdentifier
                label="Replication State"
                value={replicationState}
              />
              <DetailsIdentifier
                label="Status"
                value={
                  scanStatus === '-' ? (
                    '-'
                  ) : (
                    <Tag
                      label={scanStatus}
                      styles={{
                        tag: {
                          bg: colorOpacity(customTheme.colors.primary, 0.1),
                          border: 'none',
                        },
                        label: { color: customTheme.colors.primary },
                      }}
                    />
                  )
                }
              />
            </>
          ) : (
            <>
              <DetailsIdentifier
                label={'Size of ' + getLabel()}
                value={formatBytes(summaryData?.source_size)}
              />
              <DetailsIdentifier
                label={
                  'Number of ' +
                  (resourceType === NativeResources.SimpleStorageService
                    ? 'files in bucket'
                    : 'tables')
                }
                value={summaryData?.object_count}
              />
              <DetailsIdentifier
                label={toTitleCase(getLabel()) + ' scanned'}
                value={summaryData?.percentage_scanned + '%'}
              />
            </>
          )}
        </Stack>
      }
      label="Summary"
      isLoading={
        isLoading || ((isRepository || isDatabase) && isLoadingResourcesByGroup)
      }
    />
  );
};
